<script setup lang="ts">
import SubscriptionBadge from "~/components/voice-ui/Subscription/SubscriptionBadge.vue"
import {computed, onBeforeUnmount, ref, watch} from "vue";
import {storeToRefs} from "pinia";
import {Ban, CalendarDays, ChevronLeft, ChevronRight, History, ImageIcon, Link, MessagesSquare, MicOff, MoreHorizontal, Pencil, Plus, SearchX, Shield, Timer, UserX, Users, X} from "lucide-vue-next";
import {toast} from "vue-sonner";
import * as Sentry from "@sentry/nuxt";
import {useRouter} from "vue-router";
import {Dialog, DialogContent, DialogTitle} from "~/components/ui/dialog";
import {Drawer, DrawerContent, DrawerTitle} from "~/components/ui/drawer";
import {Avatar, AvatarFallback, AvatarImage} from "~/components/ui/avatar";
import {Badge} from "~/components/ui/badge";
import {Button} from "~/components/ui/button";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "~/components/ui/dropdown-menu";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "~/components/ui/tooltip";
import {ScrollArea} from "~/components/ui/scroll-area";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "~/components/ui/tabs";
import {Textarea} from "~/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import {resolveMediaUrl} from "~/lib/mediaCache";
import {getRepresentativeColor} from "~/lib/representativeColor";
import {INTEGRATION_BRANDS} from "~/lib/integrations/brands";
import {useUserProfileDialog} from "~/composables/useUserProfileDialog";
import {useUserStore} from "~/stores/user";
import {useGroupsStore} from "~/stores/groups";
import {useFriendsStore} from "~/stores/friends";
import {usePlatform} from "~/composables/usePlatform";
import {useMobileDrawerCloseOnBack} from "~/composables/useMobileDrawerBackNav";
import {useMobileProfileDrawer} from "~/composables/useMobileProfileDrawer";
import {PermissionFlag} from "~/lib/permissions";
import FriendshipStatusButton from "~/components/voice-ui/Friends/FriendshipStatusButton.vue";
import MessageSearchHit from "~/components/voice-ui/Chat/search/MessageSearchHit.vue";
import {Skeleton} from "~/components/ui/skeleton";
import {Spinner} from "~/components/ui/spinner";
import {useUserMessages} from "~/composables/useUserMessages";
import {pluralRu} from "~/lib/chat/activityStatus";
import type {MessageSearchHas, MessageSearchHit as Hit} from "~/stores/messageSearch";
import ScrollFade from "~/components/voice-ui/ScrollFade.vue";

const {isOpen, user, close} = useUserProfileDialog();
const {isMobile} = usePlatform();
// This dialog has no list -> detail navigation of its own, so the Android/gesture back button
// just needs to close it — see useMobileDrawerBackNav.ts.
useMobileDrawerCloseOnBack({isOpen, enabled: isMobile});

const router = useRouter();
const {user: currentUser} = storeToRefs(useUserStore());
const groupsStore = useGroupsStore();

const isSelf = computed(() => !!user.value && user.value.id === currentUser.value?.id);
const isBot = computed(() => !!user.value?.is_bot);

const name = computed(() => {
  const u = user.value;
  if (!u) return "";
  return u.display_name || u.username || u.email || "Неизвестный пользователь";
});

const subtitle = computed(() => {
  const u = user.value;
  if (!u) return "";
  return `@${u.username}`;
});

const initials = computed(() => {
  return name.value
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join("");
});

const activeGroupId = computed(() => groupsStore.selectedGroupId);

// The member record (with live `role_ids`) is looked up fresh from the group's member cache
// rather than trusting whatever was passed into `open(user)` — that way `assignRole`/`removeRole`
// mutating the cache in place is immediately reflected here, regardless of which of the three
// call sites (messages/members list/voice roster) opened the dialog.
const groupMember = computed(() => {
  const groupId = activeGroupId.value;
  if (!groupId || !user.value) return null;
  return groupsStore.groupUsers[groupId]?.find(u => u.id === user.value!.id) ?? null;
});

// Same freshness reasoning as `groupMember` above — prefer the live member-cache copy over
// whatever avatar URL was current when `open(user)` was called.
const avatarUrl = computed(() => resolveMediaUrl(groupMember.value?.avatar_url || user.value?.avatar_url, 'avatars'));

// The own banner comes from the user store: it's what upload/delete refresh, while the member cache
// only catches up through the UserProfileUpdated socket event. The backend already blanks the URL
// once the subscription that unlocked it has lapsed, so an empty string means "no banner to show".
const bannerUrl = computed(() => {
  const remote = isSelf.value
      ? currentUser.value?.banner_url
      : groupMember.value?.banner_url || user.value?.banner_url;
  return resolveMediaUrl(remote, 'banners');
});

// Same freshness reasoning again, and more: the user object handed to `open(user)` by some call
// sites (message authors, the voice roster) carries no description at all, so the bio has to come
// from the user store (self) or the group member cache.
const bio = computed(() => {
  if (isSelf.value) return currentUser.value?.description || "";
  return groupMember.value?.description || user.value?.description || "";
});

// Separate from the short "О себе" description; read from the same sources in the same order.
const biography = computed(() => {
  if (isSelf.value) return currentUser.value?.biography || "";
  return groupMember.value?.biography || user.value?.biography || "";
});

const memberRoleIds = computed(() => groupMember.value?.role_ids ?? user.value?.role_ids ?? []);

// `@everyone` is implicit for every member — showing/assigning it individually would be noise.
// The backend returns roles ascending by `position` (bigger string = more senior); reversed here
// so the assign-role dropdown and the member's role badges both show the most senior role first,
// matching the usual "top = senior" convention instead of listing junior-most first.
const assignableGroupRoles = computed(() => {
  const groupId = activeGroupId.value;
  if (!groupId) return [];
  return (groupsStore.groupRoles[groupId] ?? []).filter(r => r.name !== "@everyone").reverse();
});

const userRoles = computed(() => assignableGroupRoles.value.filter(r => memberRoleIds.value.includes(r.id)));

// The details card is tinted by a representative colour: its gradient border and a soft ambient
// glow along the bottom. Taken from the banner when there is one (it's the big picture the card
// sits on), otherwise from the avatar. When neither colour can be read (no image, a load failure,
// or a cross-origin one without CORS tainting the canvas), the member's most senior coloured role
// stands in (`userRoles` is senior-first); with none of them, the plain neutral border, no glow.
const accentImageUrl = computed(() => bannerUrl.value || avatarUrl.value);
const imageColor = ref<string | null>(null);

watch(accentImageUrl, (url) => {
  imageColor.value = null;
  if (!url) return;
  getRepresentativeColor(url)
      // A slower answer for a previous image mustn't overwrite the current one.
      .then(color => { if (accentImageUrl.value === url) imageColor.value = color; })
      .catch(() => {});
}, {immediate: true});

const accentColor = computed(() => imageColor.value ?? userRoles.value.find(r => r.color)?.color ?? null);

// Gradients can't be transitioned, so the tinted border and glow are separate layers faded in by
// opacity. The last colour is kept while fading out, and `accentReady` holds them invisible until
// the frame after the dialog opens — the dialog content is remounted on every open, and a colour
// already cached would otherwise just appear at full strength with nothing to transition from.
const lastAccentColor = ref<string | null>(null);
watch(accentColor, (color) => { if (color) lastAccentColor.value = color; }, {immediate: true});

const accentReady = ref(false);
watch(isOpen, (open) => {
  accentReady.value = false;
  if (open) requestAnimationFrame(() => requestAnimationFrame(() => { accentReady.value = true; }));
}, {immediate: true});

const accentVisible = computed(() => accentReady.value && !!accentColor.value);

const detailsBorderStyle = computed(() => lastAccentColor.value
    ? {backgroundImage: `linear-gradient(to bottom, transparent, color-mix(in srgb, ${lastAccentColor.value} 70%, transparent))`}
    : undefined);

const ambientGlowStyle = computed(() => lastAccentColor.value
    ? {backgroundImage: `radial-gradient(120% 70% at 50% 100%, color-mix(in srgb, ${lastAccentColor.value} 22%, transparent), transparent 70%)`}
    : undefined);
const rolesToAssign = computed(() => assignableGroupRoles.value.filter(r => !memberRoleIds.value.includes(r.id)));

const canManageRoles = computed(() => {
  const groupId = activeGroupId.value;
  if (!groupId) return false;
  return groupsStore.hasGroupPermission(groupId, PermissionFlag.ManageRoles);
});

function hasPermissionOnOther(flag: number) {
  const groupId = activeGroupId.value;
  if (!groupId || isSelf.value) return false;
  return groupsStore.hasGroupPermission(groupId, flag);
}

const canKick = computed(() => hasPermissionOnOther(PermissionFlag.KickMembers));
const canBan = computed(() => hasPermissionOnOther(PermissionFlag.BanMembers));

// Voice/timeout moderation isn't wired up yet (mock blocks in the tab), but the permission alone
// is still enough to show the tab, so moderators see where it'll live.
const canModerateVoice = computed(() =>
    [PermissionFlag.MuteMembers, PermissionFlag.DeafenMembers, PermissionFlag.MoveMembers]
        .some(flag => hasPermissionOnOther(flag)));

// The "Модерация" tab holds role management too, so ManageRoles alone shows it — including on your
// own profile (assigning yourself a role is allowed); kick/ban never apply to yourself.
const canModerate = computed(() => canManageRoles.value || canKick.value || canBan.value || canModerateVoice.value);

// Backend timestamps arrive as ISO strings; anything unparsable is treated as missing.
function formatDate(value?: string) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("ru-RU", {day: "numeric", month: "long", year: "numeric"});
}

const joinedAt = computed(() => formatDate(groupMember.value?.created_at || user.value?.created_at));
const lastActivity = computed(() => formatDate(groupMember.value?.last_activity || user.value?.last_activity));

// `common.UserStatus`: ONLINE = 0, OFFLINE = 1, DO_NOT_DISTURB = 2, INVISIBLE = 3, IDLE = 4 — same
// mapping as ChannelMembersList. INVISIBLE reads as offline to everyone else.
const presence = computed(() => {
  const status = groupMember.value?.user_status ?? user.value?.user_status;
  if (status === 0) return {label: "В сети", color: "bg-emerald-500"};
  if (status === 4) return {label: "Нет на месте", color: "bg-amber-500"};
  if (status === 2) return {label: "Не беспокоить", color: "bg-red-500"};
  if (status === 1 || status === 3) return {label: "Не в сети", color: "bg-muted-foreground/50"};
  return null;
});

const pendingModeration = ref<"kick" | "ban" | null>(null);

async function confirmModeration() {
  const action = pendingModeration.value;
  const groupId = activeGroupId.value;
  const target = user.value;
  pendingModeration.value = null;
  if (!action || !groupId || !target) return;
  try {
    if (action === "kick") {
      await groupsStore.kickUser(groupId, target.id);
      toast.success(`${name.value} исключён(а) из группы`);
    } else {
      await groupsStore.banUser(groupId, target.id);
      toast.success(`${name.value} забанен(а) в группе`);
    }
    close();
  } catch (e: any) {
    console.error(`Failed to ${action} user`, e);
    Sentry.captureException(e, {extra: {action: `${action}_user_from_profile`, groupId, userId: target.id}});
    toast.error(action === "kick" ? "Не удалось исключить пользователя" : "Не удалось забанить пользователя");
  }
}

async function handleAssignRole(roleId: string) {
  const groupId = activeGroupId.value;
  const targetUserId = user.value?.id;
  if (!groupId || !targetUserId) return;
  try {
    await groupsStore.assignRole(groupId, targetUserId, roleId);
  } catch (e: any) {
    console.error("Failed to assign role:", e);
    Sentry.captureException(e, {extra: {action: "assign_role", groupId, targetUserId, roleId}});
    toast.error(`Не удалось назначить роль: ${e.message || String(e)}`);
  }
}

async function handleRemoveRole(roleId: string) {
  const groupId = activeGroupId.value;
  const targetUserId = user.value?.id;
  if (!groupId || !targetUserId) return;
  try {
    await groupsStore.removeRole(groupId, targetUserId, roleId);
  } catch (e: any) {
    console.error("Failed to remove role:", e);
    Sentry.captureException(e, {extra: {action: "remove_role", groupId, targetUserId, roleId}});
    toast.error(`Не удалось снять роль: ${e.message || String(e)}`);
  }
}

function handleOpenChange(value: boolean) {
  if (!value) close();
}

const mobileProfileDrawer = useMobileProfileDrawer();

// Mobile has no /settings page of its own: profile editing lives in the profile Drawer's
// "Редактировать профиль" page (MobileProfileDrawer.vue), so open that on top of this card — it
// stays open underneath as the next card in the stack (useDrawerStack.ts) and comes back forward
// once the editor is closed. Falls back to the route only where no profile Drawer is mounted
// (bottom nav hidden).
function goToSettings() {
  if (isMobile.value && mobileProfileDrawer.isAvailable()) {
    mobileProfileDrawer.openPage("edit");
    return;
  }
  close();
  router.push("/settings");
}

// The integration tile currently swiped up. It stays up for INTEGRATION_RELEASE_DELAY after the
// pointer leaves, so a «Подключить» button the pointer just slipped off is still there to click.
const INTEGRATION_RELEASE_DELAY = 900;
const activeIntegration = ref<string | null>(null);
let integrationReleaseTimer: ReturnType<typeof setTimeout> | undefined;

function holdIntegration(name: string) {
  clearTimeout(integrationReleaseTimer);
  activeIntegration.value = name;
}

function releaseIntegration(name: string) {
  clearTimeout(integrationReleaseTimer);
  integrationReleaseTimer = setTimeout(() => {
    if (activeIntegration.value === name) activeIntegration.value = null;
  }, INTEGRATION_RELEASE_DELAY);
}

onBeforeUnmount(() => clearTimeout(integrationReleaseTimer));
// A tile left swiped up when the dialog closed shouldn't greet the next open that way.
watch(isOpen, () => {
  clearTimeout(integrationReleaseTimer);
  activeIntegration.value = null;
});

// --- «Модерация» sub-pages: Роли / Сообщения / Ссылки -------------------------------------------
// The tab is a two-pane pager like the mobile profile drawer (MobileProfileDrawer.vue): the row
// list, and a page that slides in from the right. `shownModerationPage` keeps the last page's
// content on screen while it slides back out.
type ModerationPage = "roles" | "messages" | "media" | "links";
const MODERATION_PAGE_TITLES: Record<ModerationPage, string> = {
  roles: "Роли",
  messages: "Сообщения",
  media: "Медиаконтент",
  links: "Ссылки",
};

// «Есть:» filter per search page — all three are the member's messages, narrowed server-side.
const MODERATION_PAGE_HAS: Record<Exclude<ModerationPage, "roles">, MessageSearchHas[]> = {
  messages: [],
  media: ["image"],
  links: ["link"],
};

const activeTab = ref("main");
const moderationPage = ref<ModerationPage | null>(null);
const shownModerationPage = ref<ModerationPage | null>(null);
const userMessages = useUserMessages();

const userMessagesLabel = computed(() => {
  const n = userMessages.total.value;
  const shown = n >= 10000 ? "10 000+" : n.toLocaleString("ru-RU");
  switch (shownModerationPage.value) {
    case "links":
      return `${shown} ${pluralRu(n, "сообщение со ссылкой", "сообщения со ссылками", "сообщений со ссылками")}`;
    case "media":
      return `${shown} ${pluralRu(n, "сообщение с изображениями", "сообщения с изображениями", "сообщений с изображениями")}`;
    default:
      return `${shown} ${pluralRu(n, "сообщение", "сообщения", "сообщений")}`;
  }
});

function loadUserMessages(page: ModerationPage) {
  const groupId = activeGroupId.value;
  const target = user.value;
  if (!groupId || !target || page === "roles") return;
  void userMessages.load(groupId, target.id, MODERATION_PAGE_HAS[page]);
}

// «Медиаконтент» shows the images themselves, not message previews: every image attachment of the
// loaded messages, in message order, each opening its message in the chat.
const userMediaItems = computed(() => userMessages.hits.value.flatMap(hit =>
    (hit.message.attachments ?? [])
        .filter(attachment => (attachment.content_type ?? "").startsWith("image/"))
        .map(attachment => ({hit, attachment}))));

function openModerationPage(page: ModerationPage) {
  moderationPage.value = page;
  shownModerationPage.value = page;
  loadUserMessages(page);
}

function closeModerationPage() {
  moderationPage.value = null;
}

// Esc on a «Модерация» sub-page steps back to the row list instead of closing the whole dialog.
// Menus and the confirm dialog on top handle their own Esc first and never get here.
function handleEscape(event: KeyboardEvent) {
  if (!moderationPage.value) return;
  event.preventDefault();
  closeModerationPage();
}

// Picking another tab, another profile or closing the dialog starts the tab over at its row list.
function resetModerationPages() {
  moderationPage.value = null;
  shownModerationPage.value = null;
  userMessages.reset();
}
watch(activeTab, resetModerationPages);
watch([isOpen, () => user.value?.id], () => {
  activeTab.value = "main";
  resetModerationPages();
});

// A result opens its message in the chat through the `?channel=…&message=…` route the search panel
// and push notifications use (useTargetMessageNavigation.ts): channel switch, history around the
// message, scroll and flash.
function openUserMessage(hit: Hit) {
  const groupId = activeGroupId.value;
  if (!groupId) return;
  close();
  void router.push({path: `/group/${groupId}`, query: {channel: hit.message.channel_id, message: hit.message.id}});
}

// Infinite scroll: the next page as the end of the list comes into view.
const userMessagesSentinel = ref<HTMLElement | null>(null);
let userMessagesObserver: IntersectionObserver | null = null;
watch(userMessagesSentinel, (el) => {
  userMessagesObserver?.disconnect();
  userMessagesObserver = null;
  if (!el || typeof IntersectionObserver === "undefined") return;
  userMessagesObserver = new IntersectionObserver(([entry]) => {
    if (entry?.isIntersecting) void userMessages.loadMore();
  }, {rootMargin: "200px 0px"});
  userMessagesObserver.observe(el);
}, {flush: "post"});
onBeforeUnmount(() => userMessagesObserver?.disconnect());

function handleConnectIntegration(name: string) {
  toast.info(`Интеграция с ${name} появится позже`);
}

function handleMessage() {
  toast.info("Личные сообщения ещё не реализованы");
}

const friendsStore = useFriendsStore();

async function handleBlock() {
  const target = user.value;
  if (!target) return;
  try {
    await friendsStore.blockUser(target.username);
    toast.success(`${name.value} заблокирован(а)`);
    close();
  } catch (e: any) {
    console.error("Failed to block user", e);
    Sentry.captureException(e, {extra: {action: "block_user_from_profile", userId: target.id}});
    toast.error(e?.message || "Не удалось заблокировать пользователя");
  }
}
</script>

<template>
  <!-- Raised above the default `z-50` every drawer/dialog ships with: this one is mounted in
       `app.vue`, so its portal anchor is planted in `<body>` at startup — long before a page-level
       drawer (the mobile chat in `pages/m-groups/[id].vue`) creates its own. With equal z-index the
       later DOM node wins, which put the profile *underneath* the chat that opened it. -->
  <Drawer v-if="isMobile" :open="isOpen" @update:open="handleOpenChange">
    <DrawerContent class="max-h-[90vh] z-[70]" overlay-class="z-[70]">
      <DrawerTitle class="sr-only">Профиль пользователя</DrawerTitle>

      <ScrollArea v-if="user" class="min-h-0 flex-1">
        <div class="relative flex w-full flex-col items-center px-6 pb-8 pt-2">
          <div v-if="bannerUrl" class="h-28 w-full overflow-hidden rounded-2xl bg-muted">
            <img :src="bannerUrl" alt="Баннер профиля" class="size-full object-cover" decoding="async">
          </div>

          <TooltipProvider v-if="isSelf">
            <Tooltip>
              <TooltipTrigger as-child>
                <Button class="absolute right-4 top-0 z-10" size="icon" variant="outline" @click="goToSettings">
                  <Pencil class="size-4"/>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Изменить профиль</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Avatar class="size-20 border" :class="bannerUrl && '-mt-10 border-4 border-background'">
            <AvatarImage v-if="avatarUrl" :src="avatarUrl" alt="Аватар пользователя"/>
            <AvatarFallback class="text-xl font-medium">{{ initials }}</AvatarFallback>
          </Avatar>
          <span class="mt-3 flex items-center gap-1.5 text-xl font-medium tracking-tight">
            {{ name }}
            <SubscriptionBadge :subscription="user?.subscription" :user-id="user?.id" class="size-4" />
          </span>
          <span v-if="subtitle" class="mt-1 text-muted-foreground">{{ subtitle }}</span>

          <div class="mt-6 w-full">
            <h2 class="text-base font-medium">О себе</h2>
            <p class="mt-1 text-sm text-muted-foreground">
              {{ user.description || "Пользователь пока не добавил информацию о себе." }}
            </p>
          </div>

          <div v-if="biography" class="mt-6 w-full">
            <h2 class="text-base font-medium">Биография</h2>
            <p class="mt-1 text-sm whitespace-pre-line break-words text-muted-foreground">{{ biography }}</p>
          </div>

          <div v-if="activeGroupId" class="mt-6 w-full">
            <h2 class="text-base font-medium">Роли в группе</h2>
            <div class="mt-2 flex flex-wrap items-center gap-1.5">
              <Badge v-for="role in userRoles" :key="role.id" variant="secondary" class="gap-1.5 py-1 pr-1 pl-2">
                <span class="size-2 shrink-0 rounded-full" :style="{backgroundColor: role.color || '#99aab5'}"/>
                {{ role.name }}
                <button
                    v-if="canManageRoles"
                    type="button"
                    class="relative ml-0.5 shrink-0 cursor-pointer rounded-full p-0.5 hover:bg-foreground/10 after:absolute after:-inset-1.5 after:content-['']"
                    aria-label="Снять роль"
                    @click="handleRemoveRole(role.id)"
                >
                  <X class="size-3"/>
                </button>
              </Badge>

              <DropdownMenu v-if="canManageRoles && rolesToAssign.length">
                <DropdownMenuTrigger as-child>
                  <Badge variant="outline" class="cursor-pointer gap-1 py-1" role="button">
                    <Plus class="size-3"/>
                  </Badge>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem v-for="role in rolesToAssign" :key="role.id" @select="(event: Event) => { event.preventDefault(); handleAssignRole(role.id); }">
                    <span class="size-2 shrink-0 rounded-full" :style="{backgroundColor: role.color || '#99aab5'}"/>
                    <span>{{ role.name }}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <span v-if="userRoles.length === 0 && !canManageRoles" class="text-sm text-muted-foreground">Нет ролей</span>
            </div>
          </div>

          <!-- Two rows, not one: at phone width "Написать" + the friendship button ("Удалить из
               друзей" with its icon, at `xl`) + the "…" button don't fit side by side, and the
               `whitespace-nowrap` buttons can't shrink, so the "…" was pushed off the right edge. -->
          <div class="mt-6 grid w-full grid-cols-[1fr_auto] items-center gap-2">
            <Button v-if="!isSelf && !isBot" size="xl" class="min-w-0 rounded-xl" @click="handleMessage">Написать</Button>
            <Button v-else-if="isSelf" size="xl" class="min-w-0 rounded-xl" variant="outline" @click="goToSettings">Изменить профиль</Button>
            <div v-else/>

            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button size="icon" variant="secondary" class="size-14 rounded-xl">
                  <MoreHorizontal/>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent class="w-fit">
                <DropdownMenuItem v-if="!isSelf" variant="destructive" @click="handleBlock">
                  <span>Заблокировать</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <!-- The button's row is held at its full height (`min-h-14` = `size="xl"`) while the
                 friendship status loads: the small spinner standing in for it made the sheet ~40px
                 shorter, and the answer usually arrives mid-slide — the sheet then grew to fit
                 while it was still sliding in. -->
            <div v-if="!isSelf && !isBot" class="col-span-2 flex min-h-14 items-center justify-center">
              <FriendshipStatusButton :user-id="user.id" :username="user.username" size="xl" class="w-full min-w-0 rounded-xl"/>
            </div>
          </div>
        </div>
      </ScrollArea>
    </DrawerContent>
  </Drawer>

  <Dialog v-else :open="isOpen" @update:open="handleOpenChange">
    <!-- The banner sits inset inside the card (p-1.5 frame) with its own rounded corners, instead of
         bleeding to the edges; the avatar overlaps its lower-left corner. -->
    <!-- No corner close button: the friend pill owns the banner's top-right corner; Esc and an
         outside click still close the dialog. -->
    <!-- Sized as a share of the screen (like EditGroupDialog), capped at 64rem × 760px, so it
         shrinks proportionally on smaller windows. The height is still fixed for a given window, so
         switching tabs doesn't make the dialog jump; tab content scrolls instead. -->
    <DialogContent :show-close-button="false" class="flex h-[min(760px,85dvh)] w-[min(64rem,90vw)] max-w-none flex-col gap-0 overflow-hidden rounded-[28px] p-1.5 sm:max-w-none" @escape-key-down="handleEscape">
      <DialogTitle class="sr-only">Профиль пользователя</DialogTitle>

      <template v-if="user">
        <!-- The banner keeps a fixed height and sits behind; the profile card is in normal flow, so
             a card taller than the banner hangs below it and grows the dialog, not the banner. -->
        <div class="relative flex min-h-0 flex-1 flex-col p-1.5 pt-42">
          <div class="absolute inset-x-0 top-0 h-52 overflow-hidden rounded-[22px] bg-gradient-to-br from-muted to-muted/40">
            <img v-if="bannerUrl" :src="bannerUrl" alt="Баннер профиля" class="size-full object-cover" decoding="async">
          </div>

          <!-- The Tabs root is the whole 2×2 layout, so the tab bar and its content can live in
               different cells:
                 row 1: one pill across both columns — avatar, names, tab bar
                 row 2: details card       | tab content (fills the fixed-height dialog, scrolls)
               The left column has a fixed 21rem width.
               Blocks marked «Скоро» are visual mocks with nothing behind them on the backend yet.
               The pill has no border; the details card's 1px outer layer is its border: a vertical
               gradient, transparent at the top and solid at the bottom. -->
          <Tabs v-model="activeTab" class="relative grid min-h-0 flex-1 grid-cols-[21rem_minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)] gap-1.5">
              <div class="col-span-2 row-start-1 flex">
                <div class="flex min-w-0 flex-1 items-center gap-3 rounded-full bg-background p-2">
                  <Avatar class="size-9 shrink-0">
                    <AvatarImage v-if="avatarUrl" :src="avatarUrl" alt="Аватар пользователя"/>
                    <AvatarFallback class="text-base font-medium">{{ initials }}</AvatarFallback>
                  </Avatar>

                  <h2 class="flex min-w-0 items-center gap-1.5 text-base leading-[1.15] font-semibold tracking-tight">
                    <span class="truncate">{{ name }}</span>
                    <SubscriptionBadge :subscription="user.subscription" :user-id="user.id" class="size-4 shrink-0"/>
                  </h2>

                  <TabsList class="ml-auto h-10 shrink-0 rounded-full bg-transparent">
                    <TabsTrigger value="main" class="rounded-full px-3 data-[state=active]:bg-muted data-[state=active]:shadow-none">Главная</TabsTrigger>
                    <TabsTrigger v-if="canModerate" value="moderation" class="rounded-full px-3 data-[state=active]:bg-muted data-[state=active]:shadow-none">Модерация</TabsTrigger>
                    <TabsTrigger value="integrations" class="rounded-full px-3 data-[state=active]:bg-muted data-[state=active]:shadow-none">Интеграции</TabsTrigger>
                    <TabsTrigger value="notes" class="rounded-full px-3 data-[state=active]:bg-muted data-[state=active]:shadow-none">Заметки</TabsTrigger>
                  </TabsList>
                </div>
              </div>

              <div class="relative col-start-1 row-start-2 flex min-w-0 rounded-[17px] bg-gradient-to-b from-transparent to-border p-px">
                <!-- Tinted border, cross-faded over the neutral one. -->
                <div
                    v-if="detailsBorderStyle"
                    aria-hidden="true"
                    class="pointer-events-none absolute inset-0 rounded-[17px] transition-opacity duration-700"
                    :class="accentVisible ? 'opacity-100' : 'opacity-0'"
                    :style="detailsBorderStyle"
                />
                <!-- `isolate` makes the card its own stacking context, so the `-z-10` glow paints
                     above the card's background but under its content. -->
                <div class="relative isolate flex min-w-0 flex-1 flex-col gap-3 rounded-[16px] bg-background p-4 pt-0">
                  <div
                      v-if="ambientGlowStyle"
                      aria-hidden="true"
                      class="pointer-events-none absolute inset-0 -z-10 rounded-[16px] transition-opacity duration-700"
                      :class="accentVisible ? 'opacity-100' : 'opacity-0'"
                      :style="ambientGlowStyle"
                  />
                  <p class="truncate text-sm text-muted-foreground">@{{ user.username }}</p>

                  <div v-if="bio">
                    <h3 class="text-xs font-medium text-muted-foreground">О себе</h3>
                    <p class="mt-1 text-sm break-words whitespace-pre-line">{{ bio }}</p>
                  </div>

                  <div v-if="biography">
                    <h3 class="text-xs font-medium text-muted-foreground">Биография</h3>
                    <p class="mt-1 text-sm break-words whitespace-pre-line">{{ biography }}</p>
                  </div>

                  <!-- Roles can be added and removed here too, as in the "Модерация" tab. The "+" goes
                       first so it stays put while added roles append after it — an open menu anchored
                       to a "+" that moves with every role would jump around. Tooltip outside,
                       DropdownMenu inside: the dropdown trigger anchors to the nearest Popper root.
                       Capped at ~4 rows of badges (`max-h-[112px]`), the rest scrolls with edge fades; scrolling
                       pops the list out over the card, up to `max-h-64`, without moving the layout —
                       and wider to the right (`-right-4`, flush with the card edge), so its scrollbar sits on the
                       card's right edge. -->
                  <ScrollFade v-if="userRoles.length || (canManageRoles && rolesToAssign.length)" viewport-class="max-h-[112px]" expanded-viewport-class="max-h-64" expanded-class="-right-4" class="flex flex-wrap items-center gap-1.5 pr-3">
                    <TooltipProvider v-if="canManageRoles && rolesToAssign.length">
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <span class="inline-flex">
                            <DropdownMenu>
                              <DropdownMenuTrigger as-child>
                                <Badge as="button" type="button" variant="outline" class="size-6 cursor-pointer justify-center rounded-full p-0" aria-label="Добавить роль">
                                  <Plus class="size-3"/>
                                </Badge>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent side="bottom" align="start">
                                <DropdownMenuItem v-for="role in rolesToAssign" :key="role.id" @select="(event: Event) => { event.preventDefault(); handleAssignRole(role.id); }">
                                  <span class="size-2 shrink-0 rounded-full" :style="{backgroundColor: role.color || '#99aab5'}"/>
                                  <span>{{ role.name }}</span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>Добавить роль</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <Badge v-for="role in userRoles" :key="role.id" variant="secondary" class="max-w-full shrink gap-1.5 py-1 pl-2" :class="canManageRoles && 'pr-1'" :title="role.name">
                      <span class="size-2 shrink-0 rounded-full" :style="{backgroundColor: role.color || '#99aab5'}"/>
                      <span class="truncate">{{ role.name }}</span>
                      <button
                          v-if="canManageRoles"
                          type="button"
                          class="relative ml-0.5 shrink-0 cursor-pointer rounded-full p-0.5 hover:bg-foreground/10 after:absolute after:-inset-1.5 after:content-['']"
                          aria-label="Снять роль"
                          @click="handleRemoveRole(role.id)"
                      >
                        <X class="size-3"/>
                      </button>
                    </Badge>
                  </ScrollFade>

                  <!-- `mt-auto` keeps the actions on the card's bottom edge however much is above. -->
                  <div class="mt-auto flex items-center gap-2">
                    <Button v-if="!isSelf && !isBot" class="flex-1 rounded-full" @click="handleMessage">Написать</Button>
                    <Button v-else-if="isSelf" class="flex-1 rounded-full" @click="goToSettings">Изменить профиль</Button>
                    <div v-else class="flex-1"/>

                    <!-- Styled like the "…" button next to it (round, icon-only, secondary) whatever
                         friendship state the component is showing. -->
                    <FriendshipStatusButton
                        v-if="!isSelf && !isBot"
                        :user-id="user.id"
                        :username="user.username"
                        icon-only
                        class="rounded-full border-0 bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 dark:bg-secondary dark:hover:bg-secondary/80"
                    />

                    <DropdownMenu v-if="!isSelf">
                      <DropdownMenuTrigger as-child>
                        <Button size="icon" variant="secondary" class="rounded-full" aria-label="Ещё">
                          <MoreHorizontal/>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent class="w-fit">
                        <DropdownMenuItem variant="destructive" @click="handleBlock">
                          <span>Заблокировать</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>

              <TabsContent value="main" class="col-start-2 row-start-2 flex min-h-0 flex-col">
                <ScrollArea class="min-h-0 flex-1">
                  <!-- Bento: two explicit columns, each a stack of cards as tall as their content.
                       Explicit rather than CSS `columns`: multicol pushed the second column down
                       (a card's margin spilling over the column break). Backgrounds alternate:
                       solid, light, solid, … in reading order. -->
                  <div class="flex flex-col gap-2 pl-2 pr-3">
                    <div class="grid grid-cols-2 items-start gap-2">
                      <div class="flex flex-col gap-2">
                        <div class="rounded-[20px] bg-muted p-4">
                          <p class="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <span class="size-2 rounded-full" :class="presence?.color ?? 'bg-muted-foreground/50'"/>
                            {{ presence?.label ?? "Статус" }}
                          </p>
                          <p v-if="lastActivity" class="mt-1.5 text-sm font-medium">Был(а) {{ lastActivity }}</p>
                        </div>

                        <div class="rounded-[20px] bg-muted p-4">
                          <p class="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span class="flex items-center gap-1.5"><Users class="size-3.5"/>Общие группы</span>
                            <Badge variant="outline" class="text-[10px]">Скоро</Badge>
                          </p>
                          <div class="mt-3 flex -space-x-2">
                            <span v-for="i in 4" :key="i" class="size-8 rounded-full border-2 border-muted bg-background"/>
                          </div>
                        </div>
                      </div>

                      <div class="flex flex-col gap-2">
                        <div class="rounded-[20px] bg-muted/40 p-4">
                          <p class="flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarDays class="size-3.5"/>В Voice с</p>
                          <p class="mt-1.5 text-lg leading-tight font-semibold tracking-tight">{{ joinedAt ?? "—" }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              </TabsContent>

              <!-- Two stacked panes — the row list and a sub-page — with an iOS-style push: the page
                   slides in over the full width from the right while the list drifts a third to the
                   left and fades; going back plays it in reverse. `inert` keeps the hidden pane out
                   of tab order and clicks. -->
              <TabsContent v-if="canModerate" value="moderation" class="relative col-start-2 row-start-2 min-h-0 overflow-hidden">
                <div class="contents">
                  <div
                      class="absolute inset-0 flex min-h-0 flex-col transition-[translate,opacity] duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]"
                      :class="moderationPage ? '-translate-x-1/3 opacity-0' : 'translate-x-0 opacity-100'"
                      :inert="!!moderationPage"
                  >
                    <ScrollArea class="min-h-0 flex-1">
                      <!-- Grouped lists styled like the rows in the mobile profile drawer: a rounded
                           muted group, an icon, the label, a chevron. -->
                      <div class="flex flex-col gap-3 pl-2 pr-3">
                        <div class="divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/40">
                          <button v-if="canManageRoles" type="button" class="flex min-h-11 w-full cursor-pointer items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-muted active:bg-muted" @click="openModerationPage('roles')">
                            <span class="flex size-7 shrink-0 items-center justify-center text-muted-foreground">
                              <Shield class="size-4"/>
                            </span>
                            <span class="min-w-0 flex-1 truncate text-sm font-medium">Роли</span>
                            <span class="shrink-0 text-sm text-muted-foreground tabular-nums">{{ userRoles.length }}</span>
                            <ChevronRight class="size-4 shrink-0 text-muted-foreground/70"/>
                          </button>
                          <button type="button" class="flex min-h-11 w-full cursor-pointer items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-muted active:bg-muted" @click="openModerationPage('messages')">
                            <span class="flex size-7 shrink-0 items-center justify-center text-muted-foreground">
                              <MessagesSquare class="size-4"/>
                            </span>
                            <span class="min-w-0 flex-1 truncate text-sm font-medium">Сообщения</span>
                            <ChevronRight class="size-4 shrink-0 text-muted-foreground/70"/>
                          </button>
                          <button type="button" class="flex min-h-11 w-full cursor-pointer items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-muted active:bg-muted" @click="openModerationPage('media')">
                            <span class="flex size-7 shrink-0 items-center justify-center text-muted-foreground">
                              <ImageIcon class="size-4"/>
                            </span>
                            <span class="min-w-0 flex-1 truncate text-sm font-medium">Медиаконтент</span>
                            <ChevronRight class="size-4 shrink-0 text-muted-foreground/70"/>
                          </button>
                          <button type="button" class="flex min-h-11 w-full cursor-pointer items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-muted active:bg-muted" @click="openModerationPage('links')">
                            <span class="flex size-7 shrink-0 items-center justify-center text-muted-foreground">
                              <Link class="size-4"/>
                            </span>
                            <span class="min-w-0 flex-1 truncate text-sm font-medium">Ссылки</span>
                            <ChevronRight class="size-4 shrink-0 text-muted-foreground/70"/>
                          </button>
                        </div>

                      <div v-if="canKick || canBan" class="divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/40">
                        <button
                            v-if="canKick"
                            type="button"
                            class="flex min-h-11 w-full cursor-pointer items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-muted active:bg-muted"
                            @click="pendingModeration = 'kick'"
                        >
                          <span class="flex size-7 shrink-0 items-center justify-center text-muted-foreground">
                            <UserX class="size-4"/>
                          </span>
                          <span class="min-w-0 flex-1 truncate text-sm font-medium">Исключить</span>
                          <ChevronRight class="size-4 shrink-0 text-muted-foreground/70"/>
                        </button>
                        <button
                            v-if="canBan"
                            type="button"
                            class="flex min-h-11 w-full cursor-pointer items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-muted active:bg-muted"
                            @click="pendingModeration = 'ban'"
                        >
                          <span class="flex size-7 shrink-0 items-center justify-center text-muted-foreground">
                            <Ban class="size-4"/>
                          </span>
                          <span class="min-w-0 flex-1 truncate text-sm font-medium">Забанить в группе</span>
                          <ChevronRight class="size-4 shrink-0 text-muted-foreground/70"/>
                        </button>
                      </div>

                      <div v-if="!isSelf" class="divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/40">
                        <div
                            v-for="item in [
                              {label: 'Тайм-аут', icon: Timer},
                              {label: 'Заглушить', icon: MicOff},
                              {label: 'История нарушений', icon: History},
                            ]"
                            :key="item.label"
                            class="flex min-h-11 w-full items-center gap-2.5 px-3.5 py-2 text-muted-foreground"
                            aria-disabled="true"
                        >
                          <span class="flex size-7 shrink-0 items-center justify-center">
                            <component :is="item.icon" class="size-4"/>
                          </span>
                          <span class="min-w-0 flex-1 truncate text-sm font-medium">{{ item.label }}</span>
                          <Badge variant="outline" class="text-[10px]">Скоро</Badge>
                        </div>
                      </div>
                      </div>
                    </ScrollArea>
                  </div>

                  <section
                      class="absolute inset-0 flex min-h-0 flex-col gap-2 pl-2 transition-[translate,opacity] duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]"
                      :class="moderationPage ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'"
                      :inert="!moderationPage" :aria-label="shownModerationPage ? MODERATION_PAGE_TITLES[shownModerationPage] : undefined">
                    <div class="flex shrink-0 items-center gap-1 pr-3">
                      <Button variant="ghost" size="icon-sm" class="rounded-full" aria-label="Назад" @click="closeModerationPage">
                        <ChevronLeft class="size-4"/>
                      </Button>
                      <p class="min-w-0 flex-1 truncate text-sm font-medium">
                        <template v-if="shownModerationPage === 'roles'">Роли</template>
                        <template v-else-if="userMessages.isLoading.value">Поиск…</template>
                        <template v-else-if="userMessages.total.value">{{ userMessagesLabel }}</template>
                        <template v-else-if="shownModerationPage">{{ MODERATION_PAGE_TITLES[shownModerationPage] }}</template>
                      </p>
                    </div>

                    <div v-if="shownModerationPage === 'roles'" class="pr-3">
                      <div class="flex min-h-6 items-center justify-between gap-2">
                        <p class="text-xs text-muted-foreground">Назначенные роли</p>

                        <!-- In the header, not after the badges: there it moved with every role added
                             and dragged the open menu along. Tooltip outside, DropdownMenu inside — not
                             the other way round: the dropdown trigger's anchor attaches to the nearest
                             Popper root, so with the Tooltip in between it anchored to the tooltip and
                             the menu never opened. -->
                        <TooltipProvider v-if="rolesToAssign.length">
                          <Tooltip>
                            <TooltipTrigger as-child>
                              <span class="inline-flex">
                                <DropdownMenu>
                                  <DropdownMenuTrigger as-child>
                                    <Badge as="button" type="button" variant="outline" class="size-6 cursor-pointer justify-center rounded-full p-0" aria-label="Добавить роль">
                                      <Plus class="size-3"/>
                                    </Badge>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent side="bottom" align="end">
                                    <!-- `preventDefault` on select keeps the menu open, so several roles can
                                         be added in one go (each assigned one drops out of the list). -->
                                    <DropdownMenuItem v-for="role in rolesToAssign" :key="role.id" @select="(event: Event) => { event.preventDefault(); handleAssignRole(role.id); }">
                                      <span class="size-2 shrink-0 rounded-full" :style="{backgroundColor: role.color || '#99aab5'}"/>
                                      <span>{{ role.name }}</span>
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Добавить роль</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div class="mt-2 flex flex-wrap items-center gap-1.5">
                        <Badge v-for="role in userRoles" :key="role.id" variant="secondary" class="max-w-full shrink gap-1.5 py-1 pr-1 pl-2" :title="role.name">
                          <span class="size-2 shrink-0 rounded-full" :style="{backgroundColor: role.color || '#99aab5'}"/>
                          <span class="truncate">{{ role.name }}</span>
                          <button
                              type="button"
                              class="relative ml-0.5 shrink-0 cursor-pointer rounded-full p-0.5 hover:bg-foreground/10 after:absolute after:-inset-1.5 after:content-['']"
                              aria-label="Снять роль"
                              @click="handleRemoveRole(role.id)"
                          >
                            <X class="size-3"/>
                          </button>
                        </Badge>

                        <span v-if="userRoles.length === 0 && !rolesToAssign.length" class="text-sm text-muted-foreground">Нет ролей</span>
                      </div>
                    </div>

                    <ScrollFade v-else-if="shownModerationPage" root-class="min-h-0 flex-1" viewport-class="h-full" class="flex flex-col gap-1 pr-3">
                      <template v-if="userMessages.isLoading.value">
                        <div v-for="i in 4" :key="i" class="flex gap-3 rounded-xl p-3">
                          <Skeleton class="size-8 shrink-0 rounded-full"/>
                          <div class="flex-1 space-y-2">
                            <Skeleton class="h-3 w-1/3"/>
                            <Skeleton class="h-3 w-full"/>
                            <Skeleton class="h-3 w-2/3"/>
                          </div>
                        </div>
                      </template>

                      <div v-else-if="userMessages.error.value && !userMessages.hits.value.length" class="flex flex-col items-center gap-3 px-4 py-10 text-center">
                        <p class="text-sm text-muted-foreground">Не удалось загрузить {{ MODERATION_PAGE_TITLES[shownModerationPage].toLowerCase() }}</p>
                        <Button size="sm" variant="secondary" class="rounded-full" @click="loadUserMessages(shownModerationPage)">Повторить</Button>
                      </div>

                      <div v-else-if="!userMessages.hits.value.length" class="flex flex-col items-center gap-2 px-4 py-10 text-center">
                        <SearchX class="size-8 text-muted-foreground"/>
                        <p class="text-sm font-medium">{{ {messages: 'Сообщений нет', media: 'Изображений нет', links: 'Ссылок нет', roles: ''}[shownModerationPage] }}</p>
                        <p class="text-xs text-muted-foreground">Ищем по каналам группы, которые вам видны.</p>
                      </div>

                      <template v-else>
                        <div v-if="shownModerationPage === 'media'" class="grid grid-cols-3 gap-1.5">
                          <button
                              v-for="item in userMediaItems"
                              :key="item.attachment.id"
                              type="button"
                              class="aspect-square cursor-pointer overflow-hidden rounded-xl bg-muted transition-opacity hover:opacity-85"
                              :title="item.attachment.file_name"
                              @click="openUserMessage(item.hit)"
                          >
                            <img
                                :src="resolveMediaUrl(item.attachment.download_url, 'attachments')"
                                :alt="item.attachment.file_name"
                                class="size-full object-cover"
                                loading="lazy"
                                decoding="async"
                            >
                          </button>
                        </div>
                        <template v-else>
                          <MessageSearchHit
                              v-for="hit in userMessages.hits.value"
                              :key="hit.message.id"
                              :hit="hit"
                              :group-id="activeGroupId"
                              @open="openUserMessage(hit)"
                          />
                        </template>
                        <div v-if="userMessages.hasMore.value" ref="userMessagesSentinel" class="flex justify-center py-3">
                          <Spinner v-if="userMessages.isLoadingMore.value" class="size-5 text-muted-foreground"/>
                        </div>
                      </template>
                    </ScrollFade>
                  </section>
                </div>
              </TabsContent>

              <TabsContent value="integrations" class="col-start-2 row-start-2 flex min-h-0 flex-col">
                <ScrollFade root-class="min-h-0 flex-1" viewport-class="h-full" class="grid grid-cols-2 content-start gap-2 pl-2 pr-3">
                    <!-- Brand tiles in each service's own colours; still mocks, hence «Скоро». On hover
                         (or keyboard focus inside) the whole content swipes up by one row: logo and
                         badge leave through the top edge, the name rises, and a «Подключить» button
                         comes in from below. The edges are faded with a mask, like ScrollFade, and
                         the leaving/arriving rows also cross-fade, so nothing pops in or out.
                         The content column is one row (40px) taller than the tile, which is what
                         keeps the button out of view until the swipe. Hover is tracked in JS rather
                         than `:hover` so the button stays up (and clickable) for a moment after the
                         pointer leaves — see `releaseIntegration`. -->
                    <div
                        v-for="brand in INTEGRATION_BRANDS"
                        :key="brand.name"
                        class="group relative h-28 overflow-hidden rounded-[20px]"
                        :data-active="activeIntegration === brand.name"
                        :style="{background: brand.background, color: brand.foreground}"
                        @pointerenter="holdIntegration(brand.name)"
                        @pointerleave="releaseIntegration(brand.name)"
                    >
                      <div class="absolute inset-0 [mask-image:linear-gradient(to_bottom,transparent_0,black_14px,black_calc(100%-14px),transparent_100%)]">
                        <div class="flex h-[calc(100%+40px)] flex-col p-4 transition-transform duration-300 ease-out group-focus-within:-translate-y-10 group-data-[active=true]:-translate-y-10">
                          <div class="flex items-start justify-between gap-2 transition-opacity duration-300 group-focus-within:opacity-0 group-data-[active=true]:opacity-0">
                            <svg :viewBox="brand.viewBox ?? '0 0 24 24'" class="size-7 shrink-0" fill="currentColor" role="img" :aria-label="brand.name">
                              <path :d="brand.path"/>
                            </svg>
                            <span class="rounded-full bg-current/15 px-2 py-0.5 text-[10px] font-medium">Скоро</span>
                          </div>

                          <span class="mt-auto flex h-8 items-center truncate text-sm font-semibold">{{ brand.name }}</span>
                          <button
                              type="button"
                              class="mt-2 h-8 w-full shrink-0 cursor-pointer rounded-full bg-current/15 text-xs font-semibold opacity-0 transition-[opacity,background-color] duration-300 group-focus-within:opacity-100 group-data-[active=true]:opacity-100 hover:bg-current/25 focus-visible:outline-2 focus-visible:outline-current"
                              @click="handleConnectIntegration(brand.name)"
                          >
                            Подключить
                          </button>
                        </div>
                      </div>
                    </div>
                </ScrollFade>
              </TabsContent>

              <TabsContent value="notes" class="col-start-2 row-start-2 flex min-h-0 flex-col">
                <ScrollArea class="min-h-0 flex-1">
                  <div class="flex flex-col gap-2 pl-2 pr-3">
                    <div class="rounded-2xl border border-dashed p-3">
                      <p class="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Личная заметка — видна только вам</span>
                        <Badge variant="outline" class="text-[10px]">Скоро</Badge>
                      </p>
                      <Textarea class="mt-2 min-h-24 resize-none" placeholder="Например: познакомились на митапе, любит Rust" disabled/>
                    </div>
                  </div>
                </ScrollArea>
              </TabsContent>
          </Tabs>
        </div>
      </template>
    </DialogContent>
  </Dialog>

  <AlertDialog :open="!!pendingModeration" @update:open="value => { if (!value) pendingModeration = null }">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ pendingModeration === "ban" ? `Забанить ${name}?` : `Исключить ${name}?` }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ pendingModeration === "ban"
            ? "Пользователь будет исключён из группы и не сможет вернуться, пока его не разбанят."
            : "Пользователь будет исключён из группы, но сможет вернуться по приглашению." }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Отмена</AlertDialogCancel>
        <AlertDialogAction class="bg-destructive text-white hover:bg-destructive/90" @click="confirmModeration">
          {{ pendingModeration === "ban" ? "Забанить" : "Исключить" }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
